// // // // src/components/dashboard/FiltersBar/FiltersBar.jsx

// src/components/dashboard/FiltersBar/FiltersBar.jsx
import React, { useCallback, useState } from 'react';
import { 
  Paper, 
  Box, 
  Divider, 
  useTheme, 
  useMediaQuery,
  Typography,
  Chip,
  Fade,
  alpha,
  Collapse,
  Button,
  Grid
} from '@mui/material';
import { 
  FilterList, 
  DateRange, 
  ExpandMore, 
  ExpandLess,
  Refresh,
  Clear,
  Devices,
  Assessment
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useLazyGetMetricsQuery } from '../../../store/api/dashboardApi';
import { resetFilters } from '../../../store/slices/dashboardSlice';
import { debounce } from '../../../utils/debounce';
import QuickFilters from './QuickFilters';
import DateRangeFilter from './DateRangeFilter';
import ServiceCodeFilter from './ServiceCodeFilter';
import PlatformFilter from './PlatformFilter';
import ResultFilter from './ResultFilter';

const FiltersBar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const filters = useSelector((state) => state.dashboard.filters);
  const [getMetrics, { isLoading }] = useLazyGetMetricsQuery();
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Debounced API call (300ms as specified)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetMetrics = useCallback(
    debounce((filterData) => {
      setIsDebouncing(false);
      getMetrics(filterData);
    }, 300),
    [getMetrics]
  );

  // Effect to call API when filters change
  React.useEffect(() => {
    setIsDebouncing(true);
    debouncedGetMetrics(filters);
  }, [filters, debouncedGetMetrics]);

  // Count active filters
  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (filters.quickFilter) count++;
    if (filters.dateRange.start || filters.dateRange.end) count++;
    if (filters.serviceCode) count++;
    if (filters.platform) count++;
    if (filters.resultFilter) count++;
    return count;
  }, [filters]);

  // Check if any filters are active
  const hasActiveFilters = activeFiltersCount > 0;
  const showLoading = isLoading || isDebouncing;

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleRefreshData = () => {
    setIsDebouncing(true);
    getMetrics(filters);
  };

  const toggleAdvancedFilters = () => {
    setShowAdvanced(!showAdvanced);
  };

  return (
    <Fade in timeout={400}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 4,
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          backdropFilter: 'blur(10px)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main} 50%, transparent 100%)`,
            opacity: 0.3,
          }
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <FilterList sx={{ color: 'white', fontSize: '1.25rem' }} />
            </Box>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}
              >
                Filters & Analytics
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: '0.875rem' }}
              >
                Customize your data view
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeFiltersCount > 0 && (
              <Chip
                label={`${activeFiltersCount} active`}
                size="small"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '& .MuiChip-label': {
                    px: 2,
                  },
                }}
              />
            )}

            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={handleRefreshData}
              disabled={showLoading}
              sx={{
                minWidth: 'auto',
                px: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            >
              {isMobile ? '' : 'Refresh'}
            </Button>
            
            {hasActiveFilters && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Clear />}
                onClick={handleResetFilters}
                color="error"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                {isMobile ? '' : 'Clear'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Main Filters Content */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile 
              ? '1fr' 
              : isTablet 
                ? '1fr 1fr'
                : 'auto 1fr auto',
            gap: { xs: 3, sm: 4 },
            alignItems: 'start',
          }}
        >
          {/* Quick Filters */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <DateRange sx={{ color: 'primary.main', fontSize: '1rem' }} />
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}
              >
                Quick Filters
              </Typography>
            </Box>
            <QuickFilters />
          </Box>

          {!isMobile && !isTablet && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Divider 
                orientation="vertical" 
                flexItem 
                sx={{ 
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  height: 60,
                  alignSelf: 'center'
                }} 
              />
            </Box>
          )}

          {/* Date Range and Service Code */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
              gap: { xs: 3, sm: 4 },
              alignItems: 'start',
              width: '100%',
            }}
          >
            {/* Date Range Filter */}
            <Box>
              <DateRangeFilter />
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Divider 
                  orientation="vertical" 
                  flexItem 
                  sx={{ 
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                    height: 60,
                    alignSelf: 'center'
                  }} 
                />
              </Box>
            )}

            {/* Service Code Filter */}
            <Box>
              <ServiceCodeFilter />
            </Box>
          </Box>
        </Box>

        {/* Advanced Filters Toggle */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="text"
            startIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
            onClick={toggleAdvancedFilters}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {showAdvanced ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
          </Button>
        </Box>

        {/* Advanced Filters - Collapsible */}
        <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.primary.main, 0.1) }} />
            
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 2, 
                fontWeight: 600, 
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Assessment sx={{ fontSize: '1rem' }} />
              Advanced Filters
            </Typography>
            
            <Grid container spacing={3}>
              {/* Platform Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Devices sx={{ color: 'primary.main', fontSize: '1rem' }} />
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '0.875rem'
                      }}
                    >
                      Platform
                    </Typography>
                  </Box>
                  <PlatformFilter />
                </Box>
              </Grid>

              {/* Result Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Assessment sx={{ color: 'primary.main', fontSize: '1rem' }} />
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '0.875rem'
                      }}
                    >
                      Result Type
                    </Typography>
                  </Box>
                  <ResultFilter />
                </Box>
              </Grid>

              {/* Filter Summary */}
              <Grid item xs={12} md={4}>
                {hasActiveFilters && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      height: 'fit-content',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600} color="info.main" sx={{ mb: 1 }}>
                      Active Filters:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {filters.quickFilter && (
                        <Typography variant="caption" color="text.secondary">
                          Period: <strong>{filters.quickFilter.replace('_', ' ').toUpperCase()}</strong>
                        </Typography>
                      )}
                      {(filters.dateRange.start || filters.dateRange.end) && (
                        <Typography variant="caption" color="text.secondary">
                          Custom Range: <strong>Active</strong>
                        </Typography>
                      )}
                      {filters.serviceCode && (
                        <Typography variant="caption" color="text.secondary">
                          Service: <strong>{filters.serviceCode}</strong>
                        </Typography>
                      )}
                      {filters.platform && (
                        <Typography variant="caption" color="text.secondary">
                          Platform: <strong>{filters.platform.toUpperCase()}</strong>
                        </Typography>
                      )}
                      {filters.resultFilter && (
                        <Typography variant="caption" color="text.secondary">
                          Result: <strong>{filters.resultFilter.toUpperCase()}</strong>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Loading indicator overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            opacity: showLoading ? 1 : 0,
            animation: showLoading ? 'loading 2s infinite' : 'none',
            transition: 'opacity 0.3s ease',
            '@keyframes loading': {
              '0%': { transform: 'translateX(-100%)' },
              '50%': { transform: 'translateX(0%)' },
              '100%': { transform: 'translateX(100%)' },
            },
          }}
        />
      </Paper>
    </Fade>
  );
};

export default FiltersBar;

// // src/components/dashboard/FiltersBar/FiltersBar.jsx
// import React, { useCallback, useState } from 'react';
// import { 
//   Paper, 
//   Box, 
//   Divider, 
//   useTheme, 
//   useMediaQuery,
//   Typography,
//   Chip,
//   Fade,
//   alpha
// } from '@mui/material';
// import { FilterList, DateRange, Code } from '@mui/icons-material';
// import { useSelector } from 'react-redux';
// import { useLazyGetMetricsQuery } from '../../../store/api/dashboardApi';
// import { debounce } from '../../../utils/debounce';
// import QuickFilters from './QuickFilters';
// import DateRangeFilter from './DateRangeFilter';
// import ServiceCodeFilter from './ServiceCodeFilter';

// const FiltersBar = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const filters = useSelector((state) => state.dashboard.filters);
//   const [getMetrics, { isLoading }] = useLazyGetMetricsQuery();
//   const [isDebouncing, setIsDebouncing] = useState(false);

//   // Debounced API call (300ms as specified)
//   const debouncedGetMetrics = useCallback(
//     debounce((filterData) => {
//       setIsDebouncing(false);
//       getMetrics(filterData);
//     }, 300),
//     [getMetrics]
//   );

//   // Effect to call API when filters change
//   React.useEffect(() => {
//     setIsDebouncing(true);
//     debouncedGetMetrics(filters);
//   }, [filters, debouncedGetMetrics]);

//   const activeFiltersCount = React.useMemo(() => {
//     let count = 0;
//     if (filters.quickFilter) count++;
//     if (filters.dateRange.start || filters.dateRange.end) count++;
//     if (filters.serviceCode) count++;
//     return count;
//   }, [filters]);

//   const showLoading = isLoading || isDebouncing;

//   return (
//     <Fade in timeout={400}>
//       <Paper
//         elevation={0}
//         sx={{
//           p: { xs: 2, sm: 3 },
//           mb: 4,
//           borderRadius: 3,
//           background: theme.palette.mode === 'dark'
//             ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
//             : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
//           border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
//           backdropFilter: 'blur(10px)',
//           position: 'relative',
//           overflow: 'hidden',
//           '&::before': {
//             content: '""',
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             height: 1,
//             background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main} 50%, transparent 100%)`,
//             opacity: 0.3,
//           }
//         }}
//       >
//         {/* Header */}
//         <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//             <Box
//               sx={{
//                 p: 1,
//                 borderRadius: 2,
//                 background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
//               }}
//             >
//               <FilterList sx={{ color: 'white', fontSize: '1.25rem' }} />
//             </Box>
//             <Box>
//               <Typography 
//                 variant="h6" 
//                 sx={{ 
//                   fontWeight: 700,
//                   color: 'text.primary',
//                   fontSize: { xs: '1.1rem', sm: '1.25rem' }
//                 }}
//               >
//                 Filters & Analytics
//               </Typography>
//               <Typography 
//                 variant="caption" 
//                 color="text.secondary"
//                 sx={{ fontSize: '0.875rem' }}
//               >
//                 Customize your data view
//               </Typography>
//             </Box>
//           </Box>

//           {activeFiltersCount > 0 && (
//             <Chip
//               label={`${activeFiltersCount} active`}
//               size="small"
//               sx={{
//                 background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
//                 color: 'white',
//                 fontWeight: 600,
//                 fontSize: '0.75rem',
//                 '& .MuiChip-label': {
//                   px: 2,
//                 },
//               }}
//             />
//           )}
//         </Box>

//         {/* Filters Content */}
//         <Box
//           sx={{
//             display: 'grid',
//             gridTemplateColumns: isMobile 
//               ? '1fr' 
//               : 'auto 1fr auto',
//             gap: { xs: 3, sm: 4 },
//             alignItems: 'start',
//           }}
//         >
//           {/* Quick Filters */}
//           <Box>
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
//               <DateRange sx={{ color: 'primary.main', fontSize: '1rem' }} />
//               <Typography 
//                 variant="subtitle2" 
//                 sx={{ 
//                   fontWeight: 600,
//                   color: 'text.primary',
//                   fontSize: '0.875rem'
//                 }}
//               >
//                 Quick Filters
//               </Typography>
//             </Box>
//             <QuickFilters />
//           </Box>

//           {!isMobile && (
//             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//               <Divider 
//                 orientation="vertical" 
//                 flexItem 
//                 sx={{ 
//                   borderColor: alpha(theme.palette.primary.main, 0.2),
//                   height: 60,
//                   alignSelf: 'center'
//                 }} 
//               />
//             </Box>
//           )}

//           {/* Date Range and Service Code in Mobile Stack */}
//           <Box
//             sx={{
//               display: 'grid',
//               gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
//               gap: { xs: 3, sm: 4 },
//               alignItems: 'start',
//               width: '100%',
//             }}
//           >
//             {/* Date Range Filter */}
//             <Box>
//               <DateRangeFilter />
//             </Box>

//             {!isMobile && (
//               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//                 <Divider 
//                   orientation="vertical" 
//                   flexItem 
//                   sx={{ 
//                     borderColor: alpha(theme.palette.primary.main, 0.2),
//                     height: 60,
//                     alignSelf: 'center'
//                   }} 
//                 />
//               </Box>
//             )}

//             {/* Service Code Filter */}
//             <Box>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
//                 <Code sx={{ color: 'primary.main', fontSize: '1rem' }} />
//                 <Typography 
//                   variant="subtitle2" 
//                   sx={{ 
//                     fontWeight: 600,
//                     color: 'text.primary',
//                     fontSize: '0.875rem'
//                   }}
//                 >
//                   Service Code
//                 </Typography>
//               </Box>
//               <ServiceCodeFilter />
//             </Box>
//           </Box>
//         </Box>

//         {/* Loading indicator overlay - Fixed condition */}
//         <Box
//           sx={{
//             position: 'absolute',
//             bottom: 0,
//             left: 0,
//             right: 0,
//             height: 2,
//             background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
//             opacity: showLoading ? 1 : 0,
//             animation: showLoading ? 'loading 2s infinite' : 'none',
//             transition: 'opacity 0.3s ease',
//             '@keyframes loading': {
//               '0%': { transform: 'translateX(-100%)' },
//               '50%': { transform: 'translateX(0%)' },
//               '100%': { transform: 'translateX(100%)' },
//             },
//           }}
//         />
//       </Paper>
//     </Fade>
//   );
// };

// export default FiltersBar;




// // src/components/dashboard/FiltersBar/FiltersBar.jsx
// import React, { useCallback } from 'react';
// import { 
//   Paper, 
//   Box, 
//   Divider, 
//   useTheme, 
//   useMediaQuery,
//   Typography,
//   Chip,
//   Fade,
//   alpha
// } from '@mui/material';
// import { FilterList, DateRange, Code } from '@mui/icons-material';
// import { useSelector } from 'react-redux';
// import { useLazyGetMetricsQuery } from '../../../store/api/dashboardApi';
// import { debounce } from '../../../utils/debounce';
// import QuickFilters from './QuickFilters';
// import DateRangeFilter from './DateRangeFilter';
// import ServiceCodeFilter from './ServiceCodeFilter';

// const FiltersBar = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const filters = useSelector((state) => state.dashboard.filters);
//   const [getMetrics] = useLazyGetMetricsQuery();

//   // Debounced API call (300ms as specified)
//   const debouncedGetMetrics = useCallback(
//     debounce((filterData) => {
//       getMetrics(filterData);
//     }, 300),
//     [getMetrics]
//   );

//   // Effect to call API when filters change
//   React.useEffect(() => {
//     debouncedGetMetrics(filters);
//   }, [filters, debouncedGetMetrics]);

//   const activeFiltersCount = React.useMemo(() => {
//     let count = 0;
//     if (filters.quickFilter) count++;
//     if (filters.dateRange.start || filters.dateRange.end) count++;
//     if (filters.serviceCode) count++;
//     return count;
//   }, [filters]);

//   return (
//     <Fade in timeout={400}>
//       <Paper
//         elevation={0}
//         sx={{
//           p: { xs: 2, sm: 3 },
//           mb: 4,
//           borderRadius: 3,
//           background: theme.palette.mode === 'dark'
//             ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
//             : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
//           border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
//           backdropFilter: 'blur(10px)',
//           position: 'relative',
//           overflow: 'hidden',
//           '&::before': {
//             content: '""',
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             height: 1,
//             background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main} 50%, transparent 100%)`,
//             opacity: 0.3,
//           }
//         }}
//       >
//         {/* Header */}
//         <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//             <Box
//               sx={{
//                 p: 1,
//                 borderRadius: 2,
//                 background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
//               }}
//             >
//               <FilterList sx={{ color: 'white', fontSize: '1.25rem' }} />
//             </Box>
//             <Box>
//               <Typography 
//                 variant="h6" 
//                 sx={{ 
//                   fontWeight: 700,
//                   color: 'text.primary',
//                   fontSize: { xs: '1.1rem', sm: '1.25rem' }
//                 }}
//               >
//                 Filters & Analytics
//               </Typography>
//               <Typography 
//                 variant="caption" 
//                 color="text.secondary"
//                 sx={{ fontSize: '0.875rem' }}
//               >
//                 Customize your data view
//               </Typography>
//             </Box>
//           </Box>

//           {activeFiltersCount > 0 && (
//             <Chip
//               label={`${activeFiltersCount} active`}
//               size="small"
//               sx={{
//                 background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
//                 color: 'white',
//                 fontWeight: 600,
//                 fontSize: '0.75rem',
//                 '& .MuiChip-label': {
//                   px: 2,
//                 },
//               }}
//             />
//           )}
//         </Box>

//         {/* Filters Content */}
//         <Box
//           sx={{
//             display: 'grid',
//             gridTemplateColumns: isMobile 
//               ? '1fr' 
//               : 'auto 1fr auto',
//             gap: { xs: 3, sm: 4 },
//             alignItems: 'start',
//           }}
//         >
//           {/* Quick Filters */}
//           <Box>
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
//               <DateRange sx={{ color: 'primary.main', fontSize: '1rem' }} />
//               <Typography 
//                 variant="subtitle2" 
//                 sx={{ 
//                   fontWeight: 600,
//                   color: 'text.primary',
//                   fontSize: '0.875rem'
//                 }}
//               >
//                 Quick Filters
//               </Typography>
//             </Box>
//             <QuickFilters />
//           </Box>

//           {!isMobile && (
//             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//               <Divider 
//                 orientation="vertical" 
//                 flexItem 
//                 sx={{ 
//                   borderColor: alpha(theme.palette.primary.main, 0.2),
//                   height: 60,
//                   alignSelf: 'center'
//                 }} 
//               />
//             </Box>
//           )}

//           {/* Date Range and Service Code in Mobile Stack */}
//           <Box
//             sx={{
//               display: 'grid',
//               gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
//               gap: { xs: 3, sm: 4 },
//               alignItems: 'start',
//               width: '100%',
//             }}
//           >
//             {/* Date Range Filter */}
//             <Box>
//               <DateRangeFilter />
//             </Box>

//             {!isMobile && (
//               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//                 <Divider 
//                   orientation="vertical" 
//                   flexItem 
//                   sx={{ 
//                     borderColor: alpha(theme.palette.primary.main, 0.2),
//                     height: 60,
//                     alignSelf: 'center'
//                   }} 
//                 />
//               </Box>
//             )}

//             {/* Service Code Filter */}
//             <Box>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
//                 <Code sx={{ color: 'primary.main', fontSize: '1rem' }} />
//                 <Typography 
//                   variant="subtitle2" 
//                   sx={{ 
//                     fontWeight: 600,
//                     color: 'text.primary',
//                     fontSize: '0.875rem'
//                   }}
//                 >
//                   Service Code
//                 </Typography>
//               </Box>
//               <ServiceCodeFilter />
//             </Box>
//           </Box>
//         </Box>

//         {/* Loading indicator overlay */}
//         <Box
//           sx={{
//             position: 'absolute',
//             bottom: 0,
//             left: 0,
//             right: 0,
//             height: 2,
//             background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
//             opacity: 0,
//             animation: filters ? 'loading 2s infinite' : 'none',
//             '@keyframes loading': {
//               '0%': { transform: 'translateX(-100%)', opacity: 0.7 },
//               '50%': { transform: 'translateX(0%)', opacity: 1 },
//               '100%': { transform: 'translateX(100%)', opacity: 0.7 },
//             },
//           }}
//         />
//       </Paper>
//     </Fade>
//   );
// };

// export default FiltersBar;
// import React, { useCallback } from 'react';
// import { Paper, Box, Divider, useTheme, useMediaQuery } from '@mui/material';
// import { useSelector } from 'react-redux';
// import { useLazyGetMetricsQuery } from '../../../store/api/dashboardApi';
// import { debounce } from '../../../utils/debounce';
// import QuickFilters from './QuickFilters';
// import DateRangeFilter from './DateRangeFilter';
// import ServiceCodeFilter from './ServiceCodeFilter';

// const FiltersBar = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const filters = useSelector((state) => state.dashboard.filters);
//   const [getMetrics] = useLazyGetMetricsQuery();

//   // Debounced API call (300ms as specified)
//   const debouncedGetMetrics = useCallback(
//     debounce((filterData) => {
//       getMetrics(filterData);
//     }, 300),
//     [getMetrics]
//   );

//   // Effect to call API when filters change
//   React.useEffect(() => {
//     debouncedGetMetrics(filters);
//   }, [filters, debouncedGetMetrics]);

//   return (
//     <Paper
//       elevation={1}
//       sx={{
//         p: 3,
//         mb: 3,
//         borderRadius: 2,
//       }}
//     >
//       <Box
//         sx={{
//           display: 'flex',
//           flexDirection: isMobile ? 'column' : 'row',
//           gap: 3,
//           alignItems: isMobile ? 'stretch' : 'flex-start',
//         }}
//       >
//         {/* Quick Filters */}
//         <Box sx={{ flex: isMobile ? 'none' : '0 0 auto' }}>
//           <QuickFilters />
//         </Box>

//         {!isMobile && (
//           <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
//         )}

//         {/* Date Range Filter */}
//         <Box sx={{ flex: isMobile ? 'none' : '0 0 auto' }}>
//           <DateRangeFilter />
//         </Box>

//         {!isMobile && (
//           <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
//         )}

//         {/* Service Code Filter */}
//         <Box sx={{ flex: isMobile ? 'none' : '0 0 auto' }}>
//           <ServiceCodeFilter />
//         </Box>
//       </Box>
//     </Paper>
//   );
// };

// export default FiltersBar;